import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import apiClient from '../api/client';

const SocketContext = createContext(null);

export const useSocket = () => useContext(SocketContext);

const SOCKET_URL = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');

export function SocketProvider({ children }) {
  const { token, user } = useAuth();
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [presenceMap, setPresenceMap] = useState({});

  // Load full presence map from REST API
  const loadPresence = useCallback(async () => {
    try {
      const res = await apiClient.get('/chat/presence');
      const map = {};
      res.data.forEach(p => { map[p.UserID] = p.Status; });
      setPresenceMap(map);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!token || !user) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setConnected(false);
      }
      return;
    }

    // Load initial presence
    loadPresence();
    // Refresh presence every 30s
    const presenceInterval = setInterval(loadPresence, 30000);

    const socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 2000,
    });

    socket.on('connect', () => {
      console.log('🔌 Socket connected');
      setConnected(true);
    });

    socket.on('disconnect', () => {
      console.log('🔌 Socket disconnected');
      setConnected(false);
    });

    socket.on('presence:update', ({ userId, status }) => {
      setPresenceMap(prev => ({ ...prev, [userId]: status }));
    });

    socketRef.current = socket;

    return () => {
      clearInterval(presenceInterval);
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
    };
  }, [token, user, loadPresence]);

  const value = {
    socket: socketRef.current,
    connected,
    presenceMap,
    setPresenceMap,
    loadPresence,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
}
